import React, { useState } from "react";
import { Link, useLocation, useNavigate, Outlet } from "react-router-dom";
import {
  LayoutDashboard, Package, Wrench, FileText, MessageSquare,
  Image, Settings, LogOut, Menu, X, Bell, ChevronRight, Layout, Globe, Award, Search,
  Layers, FolderOpen
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";

const navItems = [
  { label: "Dashboard", path: "/admin/dashboard", icon: LayoutDashboard },
  { label: "Products", path: "/admin/products", icon: Package },
  { label: "Our Brands", path: "/admin/brands", icon: Layers },
  { label: "Services", path: "/admin/services", icon: Wrench },
  { label: "Blog", path: "/admin/blog", icon: FileText },
  { label: "Media Library", path: "/admin/media", icon: FolderOpen },
  { label: "Gallery", path: "/admin/gallery", icon: Image },
  { label: "Certifications", path: "/admin/certifications", icon: Award },
  { label: "SEO Management", path: "/admin/seo", icon: Search },
  { label: "Messages", path: "/admin/messages", icon: MessageSquare },
  { label: "CMS Editor", path: "/admin/cms", icon: Layout },
  { label: "Settings", path: "/admin/settings", icon: Settings },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const handleLogout = () => { logout(); navigate("/admin"); };
  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? "w-60" : "w-16"} bg-brand-primary flex flex-col transition-all duration-300 fixed top-0 left-0 bottom-0 z-40`}>
        {/* Logo */}
        <div className="p-4 border-b border-white/10 flex items-center justify-between">
          <Link to="/admin/dashboard" className="flex items-center gap-2.5 overflow-hidden">
            {sidebarOpen
              ? <img src="/logo-dark.png" alt="Sava Fresh" className="h-9 w-auto object-contain" />
              : <Globe className="w-7 h-7 text-white flex-shrink-0" />}
          </Link>
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-white/10 transition-colors flex-shrink-0">
            {sidebarOpen ? <X className="w-4 h-4 text-white/70" /> : <Menu className="w-4 h-4 text-white/70" />}
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {navItems.map((item) => (
            <Link key={item.path} to={item.path} title={!sidebarOpen ? item.label : undefined}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                isActive(item.path) ? "bg-white/20 text-white" : "text-white/60 hover:text-white hover:bg-white/10"
              }`}>
              <item.icon className="w-5 h-5 flex-shrink-0" />
              {sidebarOpen && <><span className="truncate">{item.label}</span>{isActive(item.path) && <ChevronRight className="w-3.5 h-3.5 ml-auto text-white/50" />}</>}
            </Link>
          ))}
        </nav>

        {/* View site link */}
        {sidebarOpen && (
          <div className="p-3 border-t border-white/10">
            <a href="/" target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-2 px-3 py-2 rounded-xl text-white/50 hover:text-white hover:bg-white/10 text-xs font-medium transition-all">
              <Globe className="w-4 h-4" /> View Live Site ↗
            </a>
          </div>
        )}

        {/* User */}
        <div className="p-3 border-t border-white/10">
          {sidebarOpen ? (
            <div className="flex items-center gap-3 p-3 bg-white/10 rounded-xl">
              <div className="w-8 h-8 bg-accent-gold rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                {user?.name?.charAt(0) || "A"}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-white truncate">{user?.name}</div>
                <div className="text-xs text-white/50 truncate">{user?.role}</div>
              </div>
              <button onClick={handleLogout} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-white/10 transition-colors" title="Logout">
                <LogOut className="w-3.5 h-3.5 text-white/60 hover:text-white" />
              </button>
            </div>
          ) : (
            <button onClick={handleLogout} className="w-full flex justify-center p-2 rounded-xl hover:bg-white/10 transition-colors" title="Logout">
              <LogOut className="w-5 h-5 text-white/60 hover:text-white" />
            </button>
          )}
        </div>
      </aside>

      {/* Main */}
      <main className={`flex-1 flex flex-col min-h-screen transition-all duration-300 ${sidebarOpen ? "ml-60" : "ml-16"}`}>
        <header className="bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between sticky top-0 z-30 shadow-soft">
          <div>
            <h2 className="font-heading font-bold text-gray-900 text-base">
              {navItems.find(n => n.path === location.pathname)?.label || "Admin"}
            </h2>
            <p className="text-xs text-gray-400">Sava Fresh Trading FZE — Admin Panel</p>
          </div>
          <div className="flex items-center gap-3">
            <button className="w-9 h-9 flex items-center justify-center rounded-xl hover:bg-gray-100 transition-colors">
              <Bell className="w-4 h-4 text-gray-500" />
            </button>
            <div className="w-9 h-9 bg-brand-primary rounded-xl flex items-center justify-center text-white text-xs font-bold">
              {user?.name?.charAt(0) || "A"}
            </div>
          </div>
        </header>
        <div className="flex-1 p-6"><Outlet /></div>
      </main>
    </div>
  );
}
